import { defineStore } from 'pinia';
import { useAuthStore, useSubmissionnStore } from '@/stores/index';
import { computed, ref } from 'vue';
import { BranchDTO, IBranchDTO, ITeamCriteriaDTO, TeamCriteriaDTO } from '@/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { addProductToRegistrationAPI, getMyRegistrationAPI, registerToBranchAPI } from '@/services';
import { useUpdateStatusStore } from './updateStatus';
import { useCastCrewStore } from './castcrew';

export const useRegistrationStore = defineStore('registraion', () => {
  const authStore = useAuthStore();

  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();
  const castCrewStore = useCastCrewStore();
  const submissionStore = useSubmissionnStore();
  const ignore = ref(false);
  const submissionId = computed(() => getMyRegistration.data.value?.submissions[0]?.id ?? '');

  const hasReleases = computed(
    () => getMyRegistration.data.value?.submissions[0]?.releasesDone || false,
  );

  const hasSelectedCriteria = computed(
    () => (getMyRegistration.data.value?.criteriaRelations?.length ?? 0) > 0,
  );

  const hasUploadedTeamAndStill = computed(() => {
    return (
      getMyRegistration.data.value?.submissions[0]?.teamVideo?.id !== undefined &&
      getMyRegistration.data.value?.submissions[0]?.teamVideo?.id !== '' &&
      getMyRegistration.data.value?.submissions[0]?.screenshot?.id !== undefined &&
      getMyRegistration.data.value?.submissions[0]?.screenshot?.id !== ''
    );
  });

  const hasMovie = computed(() => {
    return (
      (getMyRegistration.data.value?.submissions[0]?.movie?.id !== undefined &&
        getMyRegistration.data.value?.submissions[0]?.movie?.id !== '') ||
      false
    );
  });

  const hasCompletedCriteria = computed(() => {
    return (
      (
        getMyRegistration.data.value?.criteriaRelations?.filter(
          (c) =>
            c.use_of_criteria != null && c.use_of_criteria != undefined && c.use_of_criteria !== '',
        ) || []
      ).length > 0 || false
    );
  });

  const registrationCompleted = computed(() => {
    return (
      hasReleases.value &&
      hasUploadedTeamAndStill.value &&
      hasMovie.value &&
      hasCompletedCriteria.value &&
      hasSubmittedEntry.value &&
      hasCastCrew.value
    );
  });

  const getMyRegistration = useQuery({
    queryKey: ['my-registration'],
    queryFn: async (): Promise<ITeamCriteriaDTO> => {
      if (ignore.value) {
        return new TeamCriteriaDTO();
      }
      const ret = await getMyRegistrationAPI();
      return ret ? new TeamCriteriaDTO(ret) : new TeamCriteriaDTO();
    },
    enabled: computed(() => !!authStore.isLoggedIn && authStore.role === 'Team'),
  });

  const { data: mySubmission } = submissionStore.getSubmissionEntryQuery(submissionId) ?? ref(null);
  const { data: castCrew } = castCrewStore.getCastAndCrew(submissionId) ?? ref(null);
  const hasSubmittedEntry = computed(() => {
    return mySubmission.value?.submitted || false;
  });

  const hasCastCrew = computed(() => {
    return castCrew.value?.data && castCrew.value.data.length > 0;
  });

  async function clear(value: boolean) {
    if (ignore.value != value) {
      invalidateMyRegistration();
    }
    ignore.value = value;
  }

  // Mutations
  const registerToBranch = useMutation({
    mutationFn: (branchId: string) => {
      return registerToBranchAPI(branchId);
    },
    onSuccess: () => {
      ignore.value = false;
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`registration`, err);
    },
  });

  const addProductToRegistration = useMutation({
    mutationFn: (productVariantId: string) => {
      return addProductToRegistrationAPI(productVariantId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`registration`, err);
    },
  });

  const checkIfBranchIsFull = (): boolean => {
    if (ignore.value) {
      return false;
    }

    const myRegistration = queryClient.getQueryData(['my-registration']) as
      | ITeamCriteriaDTO
      | undefined;
    if (!myRegistration?.registeredBranch()) {
      return false;
    }
    const branchesCache = queryClient.getQueryData(['branch-year', null, null]) as
      | { data: IBranchDTO[] }
      | undefined;
    if (!branchesCache) {
      return false;
    }
    const availableBranches = branchesCache.data.map((branch) => new BranchDTO(branch));
    const registeredBranchId = myRegistration.registeredBranch()?.id;
    const branch = availableBranches.find((b) => b.id === registeredBranchId);
    if (!branch || !branch.branchYears?.[0]) {
      return false;
    }
    const isFull = branch.branchYears[0].isFull();
    return isFull;
  };

  const invalidateMyRegistration = () => {
    queryClient.invalidateQueries({ queryKey: ['my-registration'] });
  };
  //addProductToRegistration

  return {
    getMyRegistration,
    clear,
    ignore,
    registerToBranch,
    addProductToRegistration,
    invalidateMyRegistration,
    checkIfBranchIsFull,
    hasReleases,
    hasUploadedTeamAndStill,
    hasMovie,
    hasCompletedCriteria,
    hasSubmittedEntry,
    hasCastCrew,
    registrationCompleted,
    hasSelectedCriteria,
  };
});
